/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ContractService,
  contractServiceKeys,
} from 'api/services/httpServices/ContractService';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormValues, schema } from './schema';

import { handleTrade } from './TradeConfirmationModal';
import {
  ContractInfo,
  GetHotTokensParams,
  HotTokensSortKeys,
  PoolInfo,
} from 'api/types/httpsTypes/contracts';
import { NotificationDex } from '../components/alerts/notification';
import { DexTradeTemplate } from 'api/types/httpsTypes/dex-trade';
import { formatAddress } from 'modules/ethereum/utils';
import { useUserState } from 'modules/user/UserContext';

import {
  TokenInfoHeader,
  TokenNameHeader,
  TokenPoolInfoHeader,
} from '../research/components/TokenCard/TokenCardHeader';
import { TradeWarnings } from './components/TradeWarnings';
import { Link, useLocation } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import {
  dexTradeKeys,
  DexTradeService,
} from 'api/services/httpServices/DexTradeService';
import {
  TemplatesBar,
  useLastUsedTemplate,
} from './components/templates/TemplatesBar';
import { mizarTemplates } from './components/templates/mizar-templates';
import { getNotifications } from '../research/components/TokenCard/Sections/SecurityAlerts';
import { TerminalPage } from '../side-panels/TerminalPage';
import { PrimaryButton } from 'modules/shared-components/button/SubmitButton';
import config from 'config';
import { useHapticFeedback } from 'modules/telegram/core/useHapticFeedback';
import { screenGte, useMediaQuery } from 'modules/media/use-media-query';
import { TokenTradeInfoHeader } from '../components/TokenTradeInfoHeader';
import createPersistedState from 'use-persisted-state';
import { useSnackBar } from 'modules/layouts/SnackBar/context';
import { formatApiError } from 'helpers/api/apiErrors';
import Button from 'modules/shared-components/button/button';
import { HotTokensPeriods } from '../research/components/Wallets/usePageParams';

import {
  isTokenSuspicious,
  isTokenSuspiciousCheck,
  SuspiciousLabelAlert,
} from '../research/components/Wallets/labels';
import { AlphaProgramModal } from './alphaProgramModal';
import PageTemplate from '../components/layout';
import { TradeForm } from './components/tokenInfo/tradeForm';
import { TokenInfoTradeBody } from './components/tokenInfo/tokenInfoTradeBody';
import { LoadingComponent } from './components/loadingComponent';
import { useCacheLastVisitedAddress } from './components/tokenInfo/useChaceLastVisitedAddress';
import { useDefaults } from './components/tokenInfo/positionMapping';
import { IntroBlocker } from './components/tokenInfo/introBlocker';
import { Chains } from 'api/types/httpsTypes/d-wallets';
import {
  useChainPersistedState,
  useSelectedChain,
} from '../components/chains/getChains';
import { useEffect, useState } from 'react';

const useAlphaProgramPersistedState = createPersistedState<boolean>(
  'mizar-alpha-program'
);

export default function Trade() {
  const { chain, address } = useCacheLastVisitedAddress();
  const [selectedChain, setSelectedChain] = useChainPersistedState(Chains.Base);

  useEffect(() => {
    if (chain !== selectedChain && document.visibilityState === 'visible') {
      setSelectedChain(chain);
    }
  }, [chain, selectedChain]);

  const { user } = useUserState();
  const { isLoading: isLoadingTemplates, data: templates } = useQuery({
    queryFn: () => DexTradeService.getTemplates(),
    queryKey: dexTradeKeys.getTemplates(),
    enabled: !!user,
  });

  const { isLoading, isFetching, data, error } = useQuery({
    queryFn: () => ContractService.getContract(chain, address),
    queryKey: contractServiceKeys.getContract(chain, address),
    enabled: !!address,
    keepPreviousData: true,
  });

  const location = useLocation();
  const { state } = location as {
    state?: {
      from: string;
    };
  };
  const from = state?.from;

  const currentPage = {
    href: `/dex/snipe/new/${chain}/${address}`,
    name: data?.generalInfo.symbol || formatAddress(address, 3),
  };

  const [alphaProgram, setAlphaProgram] = useAlphaProgramPersistedState(true);
  const isDesktop = useMediaQuery(screenGte.small);

  return (
    <IntroBlocker>
      <PageTemplate
        pages={
          from
            ? [
                {
                  href: from,
                  name: 'Research',
                },
                currentPage,
              ]
            : [currentPage]
        }
      >
        <TerminalPage>
          {error && (
            <NotificationDex
              errorMessage={error}
              className="xxs:mx-2 lg:mx-0"
              type="error"
            >
              Could not load contract data.
            </NotificationDex>
          )}
          {(isLoading || (user && isLoadingTemplates)) && (
            <div className="xxs:m-2 lg:m-0">
              <LoadingComponent />
            </div>
          )}
          {alphaProgram && isDesktop && (
            <AlphaProgramModal handleClose={() => setAlphaProgram(false)} />
          )}
          {data && (!user || (user && templates)) && (
            <Snipe
              key={data.generalInfo.address}
              templates={templates || []}
              isFetching={isFetching}
              disabled={data.status === 'RUG_PULL'}
              contract={data}
            />
          )}
        </TerminalPage>
      </PageTemplate>
    </IntroBlocker>
  );
}

export function Snipe({
  contract,
  isFetching,
  disabled,
  templates: customTemplates,
}: {
  contract: ContractInfo;
  isFetching: boolean;
  disabled: boolean;
  templates: DexTradeTemplate[];
}) {
  const selectedChain = useSelectedChain();
  const isDesktop = useMediaQuery(screenGte.large);
  const [defaultTemplateId] = useLastUsedTemplate(
    mizarTemplates(contract.generalInfo.chain.key)[0].id
  );
  const allTemplates = [
    ...customTemplates,
    ...mizarTemplates(contract.generalInfo.chain.key),
  ];
  const { user } = useUserState();
  const selectedPool = contract.liquidityInfo.activePools[0] as
    | PoolInfo
    | undefined;

  const hapticFeedback = useHapticFeedback();
  const [isShortLoading, setIsShortLoading] = useState(false);
  const handleShortLoadingClick = () => {
    setIsShortLoading(true);
    setTimeout(() => setIsShortLoading(false), 100); // 100ms delay
  };

  const form = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: useDefaults(
      contract,
      allTemplates.find((x) => x.id === defaultTemplateId) || allTemplates[0],
      selectedChain
    ),
  });

  const { handleSubmit, watch } = form;

  const { addNewMessage } = useSnackBar();

  const { mutate } = useMutation({
    mutationFn: DexTradeService.create,
    onError: (err) => {
      if (config.isTelegramBrowser) {
        addNewMessage({
          message: formatApiError(
            err,
            'There was an error while creating your trade. Try again.'
          ),
          title: 'Error',
          type: 'error',
        });
      } else {
        addNewMessage({
          type: 'error',
          title: 'Error',
          message: 'There was an error while creating your trade. Try again.',
        });
      }
    },
  });

  const params: GetHotTokensParams = {
    sort: HotTokensSortKeys.TotalBuyTop,
    address: contract.generalInfo.address,
    chain: contract.generalInfo.chain.key,
    period: HotTokensPeriods.TwentyFourHour,
  };

  const { data: poolsInfoFromBe } = useQuery({
    queryKey: contractServiceKeys.getHotTokens(params),
    queryFn: () => ContractService.getHotTokens(params),
    keepPreviousData: true,
  });

  const onSubmit = handleSubmit(
    () => {
      handleTrade(form.getValues(), mutate);
      handleShortLoadingClick();
    },
    () => {
      hapticFeedback.notification('error');
    }
  );

  return (
    <form
      autoComplete="off"
      className={twMerge(isFetching && 'animate-pulse')}
      onSubmit={(e) => {
        void onSubmit(e);
      }}
    >
      <FormProvider {...form}>
        <div className="flex space-x-1">
          {isDesktop && (
            <div className="w-4/5 space-y-1 flex flex-col overflow-hidden">
              <div className="lg:h-terminal flex flex-col	 overflow-y-auto">
                <div>
                  {!isDesktop && (
                    <TokenTradeInfoHeader
                      contract={contract}
                      pool={selectedPool}
                      expandable
                    />
                  )}
                  {isDesktop && (
                    <div className="xxs:space-y-1">
                      <div className="xxs:flex space-x-1 items-center">
                        {poolsInfoFromBe && poolsInfoFromBe?.[0] && (
                          <SuspiciousLabelAlert
                            alertItems={isTokenSuspicious({
                              suspiciousBuy: poolsInfoFromBe[0].suspiciousBuy,
                              rugAddictedBuy: poolsInfoFromBe[0].rugAddictedBuy,
                              scammerBuy: poolsInfoFromBe[0].scammerBuy,
                              freshBuy: poolsInfoFromBe[0].freshBuy,
                              totalBuy: poolsInfoFromBe[0].totalBuy,
                            })}
                          />
                        )}
                        <TokenNameHeader
                          selectedPool={selectedPool}
                          contract={contract}
                        />
                      </div>

                      <TokenInfoHeader
                        selectedPool={selectedPool}
                        contract={contract}
                      />
                      <div className="pt-1 hidden lg:block">
                        <div className="flex xxs:gap-1">
                          {getNotifications(contract).notifications.map(
                            ({ type, message }) => (
                              <NotificationDex
                                size="xSmall"
                                type={type}
                                key={message}
                              >
                                {message}
                              </NotificationDex>
                            )
                          )}
                        </div>
                      </div>
                      {poolsInfoFromBe?.[0] &&
                        isTokenSuspiciousCheck(poolsInfoFromBe?.[0]) && (
                          <div className=" xxs:p-2 rounded xxs:bg-red-600 text-red-500">
                            <div>
                              Warning: the wallets trading this token look
                              suspicious!
                            </div>
                            <ul className="xxs:mb-0 xxs:pl-4">
                              {isTokenSuspicious(poolsInfoFromBe?.[0])?.map(
                                (item, index) => (
                                  <li className="" key={index}>
                                    {item}
                                  </li>
                                )
                              )}
                            </ul>
                          </div>
                        )}
                      {selectedPool && (
                        <TokenPoolInfoHeader
                          contract={contract}
                          selectedPool={selectedPool}
                        />
                      )}
                    </div>
                  )}
                </div>

                <TokenInfoTradeBody
                  poolsInfoFromBe={poolsInfoFromBe}
                  contract={contract}
                  selectedPool={selectedPool}
                />
              </div>
            </div>
          )}

          <div className="w-full lg:w-1/5 space-y-1 flex flex-col  overflow-hidden">
            <div className="lg:h-terminal flex flex-col overflow-y-auto xxs:space-y-2 lg:space-y-1">
              {!!user && (
                <TemplatesBar
                  chain={contract.generalInfo.chain.key}
                  templates={allTemplates}
                />
              )}

              <TradeForm
                sniped={false}
                contract={contract}
                cta={
                  user ? (
                    <div className="xxs:flex xxs:flex-col xxs:space-y-2 lg:space-y-1">
                      <TradeWarnings />
                      <div className=" xxs:px-2 lg:px-0 flex flex-col xxs:space-y-1">
                        {contract.status === 'RUG_PULL' && (
                          <div className="pb-1">
                            <NotificationDex
                              type="error"
                              size={isDesktop ? 'xSmall' : 'default'}
                            >
                              This token performed a rug-pull and can't be
                              traded.
                            </NotificationDex>{' '}
                          </div>
                        )}
                        {contract.tradingInfo.honeypot && !isDesktop && (
                          <div className="pb-1">
                            <NotificationDex
                              type="error"
                              size={isDesktop ? 'xSmall' : 'default'}
                            >
                              Careful: this token looks like an honeypot!
                            </NotificationDex>
                          </div>
                        )}

                        {watch('chain') === Chains.Solana &&
                          ((watch('takeProfitConfig.antiMev') &&
                            watch('takeProfitConfig.bribe') > 0.1) ||
                            (watch('buyOrder.entries.antiMev') &&
                              watch('buyOrder.entries.bribe') > 0.1) ||
                            watch('buyOrder.entries.maxBuyGasPriority') > 0.1 ||
                            watch('takeProfitConfig.maxSellGasPriority') >
                              0.1) && (
                            <NotificationDex
                              type="error"
                              className="xxs:my-2 xxs:mx-0"
                            >
                              Warning, high gas fee! Review your settings.
                            </NotificationDex>
                          )}

                        <PrimaryButton
                          disabled={disabled}
                          className="xxs:w-auto lg:!mt-auto xxs:text-base lg:text-sm"
                          loading={isFetching || isShortLoading}
                          size={isDesktop ? 'default' : 'large'}
                          type="submit"
                        >
                          TRADE
                        </PrimaryButton>
                      </div>
                    </div>
                  ) : (
                    <div className="flex">
                      <Button
                        variant="primary"
                        as={Link}
                        to="/login"
                        className="w-full lg:!mt-auto xxs:mx-2 lg:mx-0"
                        size={isDesktop ? 'default' : 'large'}
                        type="button"
                      >
                        LOGIN
                      </Button>
                    </div>
                  )
                }
              />
            </div>
          </div>
        </div>
      </FormProvider>
    </form>
  );
}
